import React, { useEffect } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  InfoIcon
} from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import config from '@epi-config';
import { Payment } from '@epi-constants/benefitTypes';
import { FormFields } from '@epi-forms/helpers';
import { getMainAvailableBalance } from '@epi-selectors/companyAccounts';
import { formatCurrencyDecimal } from '@epi-helpers/numeral';
import {
  getCustomerNumber,
  getUserCompanyPaymentMethod
} from '@epi-selectors/api';
import { PaymentMethods } from '@epi-constants/paymentMethods';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { getUserCompany } from '@epi-actions/api';

import { InvoiceForm } from './InvoiceForm';

const { enableDepositsUpdate, depositUpdateAllowedIds } = config;

export const PaymentMethod = ({
  remainingBalance,
  formattedMissingAmount
}: {
  remainingBalance: number;
  formattedMissingAmount: string;
}) => {
  const { primaryFrontColor } = useTheme();
  const dispatch = useDispatch();
  const { control, trigger } = useFormContext();
  const { t } = useTranslation();
  const balance = useSelector(getMainAvailableBalance);
  const defaultPaymentMethod = useSelector(getUserCompanyPaymentMethod);
  const companyId = useSelector(getCustomerNumber);
  const allowDepositUpdate =
    enableDepositsUpdate || depositUpdateAllowedIds.includes(companyId);
  const { isOnBoardingActive } = useOnboardingWizard();
  const notEnoughMoney = remainingBalance < 0;

  const {
    field: { value, onChange }
  } = useController({
    name: FormFields.PaymentMethod,
    control
  });

  const onPaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange(event.target.value);
    trigger();
  };

  useEffect(() => {
    dispatch(getUserCompany());
  }, []);

  return (
    <Box fontSize={16} fontWeight={400} position="relative" top={3}>
      <RadioGroup onChange={onPaymentMethodChange} value={value ?? ''}>
        <Box display="flex" flexDirection="column">
          {defaultPaymentMethod === PaymentMethods.EInvoice && (
            <Box display="flex" alignItems="center">
              <FormControlLabel
                value={Payment.EInvoice}
                control={<Radio />}
                label={t(
                  'containers.deposit_money.topup_tab.form.payment.eInvoice'
                )}
                id="choose-eInvoice"
              />
              <Tooltip
                arrow
                placement="top"
                id="e-invoice-info-tooltip"
                title={
                  <Trans
                    i18nKey={`containers.deposit_money.topup_tab.form.payment.eInvoice_info`}
                  />
                }
              >
                <Box id="e-invoice-info-icon" mb="6px">
                  <InfoIcon color={primaryFrontColor} />
                </Box>
              </Tooltip>
            </Box>
          )}
          {value === Payment.EInvoice && !allowDepositUpdate && <InvoiceForm />}
          {defaultPaymentMethod === PaymentMethods.Email && (
            <Box display="flex" alignItems="center">
              <FormControlLabel
                value={PaymentMethods.PdfInvoice}
                control={<Radio />}
                label={t(
                  'containers.deposit_money.topup_tab.form.payment.email_invoice'
                )}
                id="choose-email-invoice"
              />
              <Tooltip
                arrow
                placement="top"
                id="email-invoice-info-tooltip"
                title={
                  <Trans
                    i18nKey={`containers.deposit_money.topup_tab.form.payment.email_invoice_info`}
                  />
                }
              >
                <Box id="email-invoice-info-icon" mb="6px">
                  <InfoIcon color={primaryFrontColor} />
                </Box>
              </Tooltip>
            </Box>
          )}
          {!isOnBoardingActive && (
            <Box display="flex" alignItems="center">
              <FormControlLabel
                value={Payment.Deduction}
                disabled={notEnoughMoney}
                control={<Radio />}
                label={t(
                  'containers.deposit_money.topup_tab.form.payment.deduction'
                )}
                id="choose-deduction"
              />
              {notEnoughMoney && (
                <Tooltip
                  arrow
                  placement="top"
                  id="e-invoice-info-tooltip"
                  title={
                    <Trans
                      i18nKey={`containers.deposit_money.topup_tab.form.payment.deduction_info`}
                      values={{
                        currentBalance: formatCurrencyDecimal(balance),
                        missingAmount: formattedMissingAmount
                      }}
                    />
                  }
                >
                  <Box id="e-invoice-info-icon" mb="6px">
                    <InfoIcon color={primaryFrontColor} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </RadioGroup>
    </Box>
  );
};
